import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, Button, TextInput, ResponsiveFlex } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import ComboAccountSelector from "../../ComboAccountSelector";
import AccountContext from "../../contexts/AccountContext";
import Container from "../../Container";
import HeaderWithSteps from "../../HeaderWithSteps";
import { useDomesticWireImportContext } from "./DomesticWireImportContext";


const WirePaymentDetailsScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { accounts } = useContext(AccountContext);
  const { paymentDetails, setPaymentDetails } =
    useDomesticWireImportContext();

  const [description, setDescription] = useState(
    paymentDetails.description
  );
  const [fromAccount, setFromAccount] = useState(
    paymentDetails.fromAccount
  );
  const [internalNote, setInternalNote] = useState(
    paymentDetails.internalNote
  );
  const [errors, setErrors] = useState<{
    description?: string;
    fromAccount?: string;
  }>({});

  const validAccounts = accounts.filter(
    (a: Account) => a.isInternal() && a.isValidTransferSource()
  );

  const validate = (): boolean => {
    const next: { description?: string; fromAccount?: string } = {};
    if (!description.trim()) {
      next.description = l10n.getString(
        "wire-payment-details-error-required-title"
      );
    }
    if (!fromAccount) {
      next.fromAccount = l10n.getString(
        "wire-payment-details-error-required-account"
      );
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validate()) return;
    setPaymentDetails({ description, fromAccount, internalNote });
    navigate("../payments");
  };

  const handleBack = () => {
    navigate("../review-recipients");
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        headerText={l10n.getString("wire-payment-details-header")}
      />
      <div>
        <h1 className="fontSize--l nds-sans margin--top--xl margin--bottom--xxs">
          {l10n.getString("wire-payment-details-section-title")}
        </h1>
        <p>
          {l10n.getString("wire-payment-details-section-description")}
        </p>
        <ResponsiveFlex gapSize="l">
          <Row className="margin--top--m">
            <Row.Item>
              <TextInput
                label={l10n.getString(
                  "wire-payment-details-payment-title-label"
                )}
                maxLength={256}
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDescription(e.target.value)
                }
                error={errors.description}
              />
            </Row.Item>
          </Row>

            <Row>
              <Row.Item>
                <ComboAccountSelector
                  label={l10n.getString(
                    "wire-payment-details-originating-account-label"
                  )}
                  accounts={validAccounts}
                  isDestination={true}
                  showAddExternalAccountLink={false}
                  value={fromAccount}
                  onChange={(value: string) => {
                    setFromAccount(value);
                    if (errors.fromAccount) {
                      setErrors((prev) => ({
                        ...prev,
                        fromAccount: undefined,
                      }));
                    }
                  }}
                  error={errors.fromAccount}
                />
              </Row.Item>
            </Row>

          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString(
                  "wire-payment-details-internal-note-label"
                )}
                value={internalNote}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setInternalNote(e.target.value)
                }
              />
            </Row.Item>
          </Row>
        </ResponsiveFlex>
      </div>
      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("button-back")}
              onClick={handleBack}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default WirePaymentDetailsScreen;
